import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import Home from '../Components/Home/Home';
import Nav from '../Components/Nav/Nav';
import SignupPage from '../Components/Signup/SingupPage';
import Footer from '../Components/footer/Footer';
import NewsPage from '../Components/News/News';
import Upload from '../Components/upload/Upload';

import ChoosePlan from '../Components/ChoosePlan/ChoosePlan';
import Greetings from '../Components/Greetings/Greetings';
import PrivacyPolicy from '../Components/PrivacyPolicy/PrivacyPolicy';
import TermsService from '../Components/TermService/TermsService';
import RefundPolicy from '../Components/RefundPolicy/RefundPolicy';
import AboutUs from '../Components/About_Us/AboutUs';
import ReportOutlet from '../Components/Reports/ReportOutlet/ReportOutlet';
import ReportContent from '../Components/Reports/ReportContent/ReportContent';
import YourApproach from '../Components/Reports/ReportApproach/YourApproach';
import CorrectApproach from '../Components/Reports/ReportApproach/CorrectApproach';
import ProfileOutlet from '../Components/Profile/ProfileOutlet/ProfileOutlet';
import PersonalDetails from '../Components/Profile/PersonalDetails/PersonalDetails';
import UpdateProfile from '../Components/Profile/UpdateProfile/UpdateProfile';
import Membership from '../Components/Profile/Membership/Membership';
import ProfileReport from '../Components/Profile/ProfileReport/ProfileReport';
import Support from '../Components/Profile/Support/Support';
import SubscriptionPricing from '../Components/SubscriptionPricing/SubscriptionPricing';
import SubscriptionSuccess from '../Components/SubscriptionSuccess/SubscriptionSuccess';
import SubscriptionFailure from '../Components/SubscriptionFailure/SubscriptionFailure';
import GamePostOutlet from '../Components/GamePost/GamePostOutlet/GamePostOutlet';
import GameVideos from '../Components/GamePost/GameVideos/GameVideos';
import GameReports from '../Components/GamePost/GameReports/GameReports';
import GameMembership from '../Components/GamePost/GameMembership/GameMembership';
import GameSupport from '../Components/GamePost/GameSupport/GameSupport';
import GameFeedbackOutlet from '../Components/GameFeedback/GameFeedbackOutlet/GameFeedbackOutlet';
import GameCoachFeedback from '../Components/GameFeedback/GameCoachFeedback/GameCoachFeedback';
import Highlights from '../Components/GameFeedback/Highlights/Highlights';
import Stats from '../Components/GameFeedback/Stats/Stats';
import LandingPage from '../Components/CoachFlow/landingPage';
import Pending from '../Components/CoachFlow/Pending/Pending';
import Reviewd from '../Components/CoachFlow/Reviewd/Reviewd';
import Requestfeedback from '../Components/GamePost/RequestFeedback/Requestfeedback';
import CreateFeedback from '../Components/Video.js/create-feedback/CreateFeedback';
import ReviewFeedback from '../Components/Video.js/review-feedback/ReviewFeedback';
import UsersPostsTable from '../Components/CoachFlow/Academy/academy_report';
import Playground from '../Components/Playground/Playground';

import Products from '../Components/Products/Products';
import SmartAssessments from '../Components/Products/SmartAssessments';
import SmartInsights from '../Components/Products/SmartInsights';
import SmartJournals from '../Components/Products/SmartJournals';
import Solutions from '../Components/Solutions/Solutions';
import Customers from '../Components/Customers/Customers';
import Careers from '../Components/Careers/Careers';
import CaseStudies from '../Components/CaseStudies/CaseStudies';
import ExploreProducts from '../Components/ExploreProducts/ExploreProducts';
import ReactGA from 'react-ga4';

// Helper to scroll to top on route change
const ScrollToTop = () => {
    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    return null;
};

// Global gate to force users to complete profile on first sign-in or when required fields are missing
const ProfileGate = () => {
    const env = process.env.REACT_APP_ENV;
    const location = useLocation();
    // Hooks must be called unconditionally
    const profileValue = useSelector((state) => state.user?.profile?.value || {});
    const pd = profileValue?.profileData || {};
    const email = profileValue?.email?.email;
    const name = pd?.name || {};
    const gender = pd?.gender;
    const dob = pd?.dob;

    if (env !== 'users') return null;

    let token = null;
    // let loginCount = 0;
    let profileCompletedFlag = false;
    try {
        token = localStorage.getItem('BQS_TOKEN');
        // loginCount = parseInt(localStorage.getItem('LOGIN_COUNT') || '0', 10) || 0;
        profileCompletedFlag = localStorage.getItem('PROFILE_COMPLETED') === '1';
    } catch { }

    // If not authenticated, let existing route logic handle redirect
    if (!token) return null;

    let dobValid = false;
    if (dob && /^\d{2}-\d{2}-\d{4}$/.test(dob)) {
        const dt = DateTime.fromFormat(dob, 'dd-MM-yyyy');
        if (dt.isValid && dt <= DateTime.now().startOf('day')) dobValid = true;
    }
    const hasName = Boolean(name?.firstName && name?.lastName);
    const isComplete = Boolean(email && hasName && gender && dobValid);

    if (isComplete && !profileCompletedFlag) {
        try { localStorage.setItem('PROFILE_COMPLETED', '1'); } catch { }
    }

    let allowUpdate = false;
    try { allowUpdate = sessionStorage.getItem('ALLOW_PROFILE_UPDATE') === '1'; } catch { }

    if (!allowUpdate && location.pathname !== '/update-profile') {
        try { sessionStorage.removeItem('ALLOW_PROFILE_UPDATE'); } catch { }
    }

    return null;
};

// Track GA4 page views for each route change (works with HashRouter)
const GAListener = () => {
    const location = useLocation();
    useEffect(() => {
        const page = `${location.pathname}${location.search}${location.hash}`;
        ReactGA.send({ hitType: 'pageview', page });
    }, [location.pathname, location.search, location.hash]);
    return null;
};

// Trailing slash remover to put just inside BrowserRouter
const RemoveTrailingSlash = () => {
    const location = useLocation();
    // Only redirect non-root paths (ignore `/`)
    if (location.pathname.length > 1 && location.pathname.endsWith('/')) {
        return (
            <Navigate
                to={{
                    pathname: location.pathname.replace(/\/+$/, ''),
                    search: location.search,
                    hash: location.hash,
                }}
                replace
            />
        );
    }
    return null;
};

// Global gate to force users to upgrade if their First Step plan has expired
const SubscriptionGate = () => {
    const location = useLocation();
    const allSubs = useSelector((state) => state.user?.allSubscriptions?.value);
    const activeSubs = useSelector((state) => state.user?.subscriptions?.value);

    // Check if subscription enforcement is off
    const enforcement = (process.env.REACT_APP_SUBSCRIPTION_ENFORCEMENT || 'hard').trim().toLowerCase();
    if (enforcement === 'off') {
        return null;
    }

    let token = null;
    try { token = localStorage.getItem('BQS_TOKEN'); } catch { }

    if (!token) return null;
    
    // If not fetched yet, do nothing (wait for fetchMySubscriptions)
    if (!Array.isArray(allSubs) || !Array.isArray(activeSubs)) return null;

    // Check if they have an expired First Step plan
    const hasExpiredFirstStep = allSubs.some(sub => {
        if (sub.plan?.code === 'insights-first-step') {
            const isActive = activeSubs.some(active => active._id === sub._id);
            return !isActive;
        }
        return false;
    });

    const hasActivePlan = activeSubs.length > 0;

    // Force upgrade
    if (hasExpiredFirstStep && !hasActivePlan && location.pathname !== '/upgrade') {
        const allowedPaths = ['/login', '/signup', '/greetings', '/privacy_policy', '/term_service', '/about', '/subscription-success', '/subscription-failure'];
        if (!allowedPaths.includes(location.pathname)) {
            return <Navigate to="/upgrade" replace />;
        }
    }
    
    // If they have an active plan and try to go to upgrade, redirect out
    const isTestMode = location.search.includes("test=1");
    if (location.pathname === '/upgrade' && hasActivePlan && !isTestMode) {
        return <Navigate to="/game-posts/game-videos" replace />;
    }

    return null;
};

export default function Routers() {
    const env = process.env.REACT_APP_ENV;

    return (
        <HashRouter>
            <ScrollToTop />
            <GAListener />
            <RemoveTrailingSlash />
            {env === "users" && <ProfileGate />}
            {env === "users" && <SubscriptionGate />}
            <Nav />
            <Routes>
                <Route index element={<Home />} />
                {/* Signup should be reachable in all environments */}
                <Route path="signup" element={<SignupPage />} />
                {/* Explore Products: public route */}
                <Route path="/SmartInsights" element={<Playground />} />
                {/* Products Page */}
                <Route path="/products" element={<Products />} />
                {/* Smart Assessments Detail Page */}
                <Route path="/products/smart-assessments" element={<SmartAssessments />} />
                {/* Smart Insights Detail Page */}
                <Route path="/products/smart-insights" element={<SmartInsights />} />
                {/* Smart Journals Detail Page */}
                <Route path="/products/smart-journals" element={<SmartJournals />} />
                {/* Solutions Page */}
                <Route path="/solutions" element={<Solutions />} />
                {/* Redirect Customers to Solutions */}
                <Route path="/customers" element={<Navigate to="/solutions" replace />} />
                {/* Careers Page */}
                <Route path="/careers" element={<Careers />} />
                {/* Case Studies Page */}
                <Route path="/case-studies" element={<CaseStudies />} />
                {/* Explore Products: public route */}
                <Route path="/explore" element={<ExploreProducts />} />
                {/* Support Route */}
                <Route path="support" element={<GameSupport />} />
                {env !== "standalone" ? (
                    <>
                        {/* Update Profile: top-level route for profile completion */}
                        <Route path="update-profile" element={<UpdateProfile />} />
                        <Route path="upgrade" element={<SubscriptionPricing isForceUpgrade={true} />} />
                        <Route path="news" element={<NewsPage />} />
                        <Route path="blogs" element={<Navigate to="/news" replace />} />
                        <Route path="upload" element={<Upload />} />
                        <Route path="choose_plan" element={<ChoosePlan />} />
                        <Route path="subscription-pricing" element={<SubscriptionPricing />} />
                        <Route path="subscription-success" element={<SubscriptionSuccess />} />
                        <Route path="subscription-failure" element={<SubscriptionFailure />} />
                        {/* kept for backward compatibility; top-level route already added */}
                        <Route path="signup" element={<SignupPage />} />
                        <Route path='game-posts' element={<GamePostOutlet />}>
                            <Route index element={<Navigate to="game-videos" replace />} />
                            <Route path='game-videos' element={<GameVideos />} />
                            <Route path='game-videos/feedback/:postId' element={<ReviewFeedback />} />
                            <Route path='game-videos/request/:postId' element={<Requestfeedback />} />
                            <Route path='game-reports' element={<GameReports />} />
                            <Route path='game-reports/feedback/:postId' element={<ReviewFeedback />} />
                            <Route path='subscription-pricing' element={<SubscriptionPricing />} />
                            <Route path='game-supports' element={<GameSupport />} />
                        </Route>

                        <Route path="profile" element={<ProfileOutlet />}>
                            <Route index element={<PersonalDetails />} />
                            <Route path='personal_details' element={<PersonalDetails />} />
                            <Route path='membership' element={<Membership />} />
                            <Route path='report' element={<ProfileReport />} />
                            <Route path='support' element={<Support />} />
                        </Route>

                        <Route path="game-feedback" element={<GameFeedbackOutlet />}>
                            <Route index element={<GameCoachFeedback />} />
                            <Route path="coach-feedback" element={<GameCoachFeedback />} />
                            <Route path="highlights" element={<Highlights />} />
                            <Route path="stats" element={<Stats />} />
                        </Route>

                        <Route path="greetings" element={<Greetings />} />
                        <Route path="privacy_policy" element={<PrivacyPolicy />} />
                        <Route path="term_service" element={<TermsService />} />
                        <Route path="refund_policy" element={<RefundPolicy />} />
                        <Route path="about" element={<AboutUs />} />

                        <Route path="reports/:reviewPostId" element={<ReportOutlet />}>
                            <Route index element={<ReportContent />} />
                            <Route path="uploaded-video" element={<ReportContent />} />
                            <Route path=":technique/your-approach" element={<YourApproach />} />
                            <Route path=":technique/correct-approach" element={<CorrectApproach />} />
                        </Route>

                        <Route path="coach_flow" element={<LandingPage />}>
                            <Route index element={<Navigate to="reviewd-video" replace />} />
                            <Route path="pending-video" element={<Pending />} />
                            <Route path="pending-video/feedback/:postId" element={<CreateFeedback />} />
                            <Route path="reviewd-video" element={<Reviewd />} />
                            <Route path="reviewd-video/feedback/:postId" element={<ReviewFeedback />} />
                            <Route path="academy-reports" element={<UsersPostsTable />} />
                        </Route>
                    </>
                ) : (
                    <>
                        <Route path="game-posts" element={<GamePostOutlet />}>
                            <Route index element={<Navigate to="game-videos" replace />} />
                            <Route path="game-videos" element={<GameVideos />} />
                            <Route path='game-videos/feedback/:postId' element={<ReviewFeedback />} />
                            <Route path='game-videos/request/:postId' element={<Requestfeedback />} />
                            <Route path="game-reports" element={<GameReports />} />
                            <Route path='game-reports/feedback/:postId' element={<ReviewFeedback />} />
                            <Route path="subscription-pricing" element={<SubscriptionPricing />} />
                            <Route path="game-supports" element={<GameSupport />} />
                        </Route>
                        <Route path="coach_flow" element={<LandingPage />}>
                            <Route index element={<Navigate to="reviewd-video" replace />} />
                            <Route path="pending-video" element={<Pending />} />
                            <Route path="pending-video/feedback/:postId" element={<CreateFeedback />} />
                            <Route path="reviewd-video" element={<Reviewd />} />
                            <Route path="reviewd-video/feedback/:postId" element={<ReviewFeedback />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/" />} />
                    </>
                )}
            </Routes>
            <Footer />
        </HashRouter>
    );
}
